// Precondition: None
// Postcondition: Custom element for displaying structured information in a card layout with key-value pairs
class Card extends HTMLElement {
  private _data: Record<string, any> = {};

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes() {
    return ["title", "data"];
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  // Precondition: value is string, object, or array
  // Postcondition: Sets internal _data and re-renders component
  set data(value: Record<string, any> | string) {
    if (typeof value === "string") {
      try {
        this._data = JSON.parse(value);
      } catch (e) {
        console.error("Invalid JSON data:", e);
        this._data = {};
      }
    } else {
      this._data = value || {};
    }
    this.render();
  }

  // Precondition: None
  // Postcondition: Returns current data object
  get data(): Record<string, any> {
    return this._data || {};
  }

  // Precondition: None
  // Postcondition: Sets title attribute and re-renders
  set title(value: string) {
    this.setAttribute("title", value);
  }

  // Precondition: None
  // Postcondition: Returns current title or empty string
  get title(): string {
    return this.getAttribute("title") || "";
  }

  // Precondition: value can be null, undefined, string, array, or object
  // Postcondition: Returns formatted string representation
  private formatValue(value: any): string {
    if (value === null || value === undefined || value === "") {
      return "—";
    }

    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : "—";
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return String(value);
  }

  // Precondition: key is string in camelCase or snake_case
  // Postcondition: Returns Title Case formatted label
  private formatLabel(key: string): string {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim();
  }

  // Precondition: Shadow DOM attached
  // Postcondition: Renders card HTML structure into shadow DOM
  private render(): void {
    const title = this.getAttribute("title") || "";
    const dataAttr = this.getAttribute("data");
    let data = this._data || {};

    if (dataAttr && !this._data) {
      try {
        data = JSON.parse(dataAttr);
      } catch (e) {
        console.error("Invalid data attribute:", e);
        data = {};
      }
    }

    const entries = Object.entries(data);

    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const template = document.createElement("template");
    template.innerHTML = `
      <div class="card">
        ${
          title
            ? `
          <div class="card-header">
            <h3 class="card-title">${title}</h3>
          </div>
        `
            : ""
        }

        <div class="card-body">
          ${
            entries.length === 0
              ? `
            <div class="empty-state">
              <div class="empty-icon">📋</div>
              <p class="empty-text">No information available</p>
            </div>
          `
              : `
            <div class="info-list">
              ${entries
                .map(([key, value]) => {
                  const formattedValue = this.formatValue(value);
                  const isEmpty = formattedValue === "—";
                  return `
                  <div class="info-item">
                    <div class="info-label">${this.formatLabel(key)}</div>
                    <div class="info-value ${isEmpty ? "empty" : ""}">${formattedValue}</div>
                  </div>
                `;
                })
                .join("")}
            </div>
          `
          }
        </div>
      </div>
    `;

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = "";
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  // Precondition: None
  // Postcondition: Returns CSS string for shadow DOM styles
  private getStyles(): string {
    return `
      :host {
        display: block;
      }

      .card {
        background: var(--bg, #ffffff);
        border: 1px solid var(--border, #e5e7eb);
        border-radius: var(--radius-md, 12px);
        overflow: hidden;
      }

      .card-header {
        padding: 20px 24px;
        background: var(--header-bg, #f9fafb);
        border-bottom: 1px solid var(--border, #e5e7eb);
      }

      .card-title {
        margin: 0;
        font-size: 20px;
        font-weight: 700;
        letter-spacing: -0.015em;
        color: var(--text, #1f2937);
      }

      .card-body {
        padding: 24px;
      }

      .info-list {
        display: flex;
        flex-direction: column;
        gap: 0;
      }

      .info-item {
        display: grid;
        grid-template-columns: minmax(140px, 1fr) 2fr;
        gap: 16px;
        padding: 16px 0;
        border-bottom: 1px solid var(--border, #e5e7eb);
      }

      .info-item:last-child {
        border-bottom: none;
      }

      .info-item:first-child {
        padding-top: 0;
      }

      .info-label {
        font-weight: 600;
        color: var(--muted, #6b7280);
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        display: flex;
        align-items: flex-start;
        padding-top: 2px;
      }

      .info-value {
        color: var(--text, #1f2937);
        font-size: 14px;
        word-break: break-word;
        display: flex;
        align-items: flex-start;
        line-height: 1.5;
      }

      .info-value.empty {
        color: var(--muted, #6b7280);
      }

      .empty-state {
        text-align: center;
        padding: 48px 24px;
        color: var(--muted, #6b7280);
      }

      .empty-icon {
        font-size: 48px;
        margin-bottom: 16px;
        opacity: 0.3;
      }

      .empty-text {
        font-size: 14px;
        margin: 0;
      }

      .info-value a {
        color: var(--primary, #3b82f6);
        text-decoration: none;
        border-bottom: 1px solid transparent;
        transition: border-color 0.2s;
      }

      .info-value a:hover {
        border-bottom-color: var(--primary, #3b82f6);
      }

      @media (max-width: 640px) {
        .info-item {
          grid-template-columns: 1fr;
          gap: 8px;
        }

        .card-header {
          padding: 16px 20px;
        }

        .card-title {
          font-size: 18px;
        }

        .card-body {
          padding: 20px;
        }
      }

      @media (prefers-color-scheme: dark) {
        .card {
          background: var(--bg, #1f2937);
          border-color: var(--border, #374151);
        }

        .card-header {
          background: var(--header-bg, #111827);
        }

        .card-title {
          color: var(--text, #f3f4f6);
        }

        .info-label {
          color: var(--muted, #9ca3af);
        }

        .info-value {
          color: var(--text, #f3f4f6);
        }
      }
    `;
  }
}

// Register custom element

customElements.define("ft-card", Card);
